import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/services/api';
import { useToast } from './toast';
import { useConfirm } from './confirm';

interface ReportTarget {
  vehicleId?: string;
  compartmentId?: string;
  itemId?: string;
  warehouseItemId?: string;
}

interface ExistingReport {
  id: string;
  note: string;
  createdAt: string;
  reportedByName: string;
  resolved?: boolean;
}

/** Modal per gestire le segnalazioni di un oggetto: lista esistenti + nuova */
export function ReportModal({ target, targetLabel, onClose }: {
  target: ReportTarget; targetLabel: string; onClose: () => void;
}): React.JSX.Element {
  const [note, setNote] = useState('');
  const qc = useQueryClient();
  const toast = useToast();
  const confirm = useConfirm();

  const targetId = target.itemId ?? target.compartmentId ?? target.warehouseItemId ?? target.vehicleId ?? null;

  const { data: existing = [], refetch } = useQuery<ExistingReport[]>({
    queryKey: ['reports.list', targetId],
    queryFn: async () => {
      if (!targetId) return [];
      return (await api.get('/reports', { params: { targetId } })).data;
    },
    enabled: !!targetId,
  });

  const addMut = useMutation({
    mutationFn: async () => { await api.post('/reports', { ...target, note }); },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['reports.counts'] });
      qc.invalidateQueries({ queryKey: ['training.reports'] });
      refetch();
      setNote('');
      toast.show('Segnalazione aggiunta', 'success');
    },
  });

  const delMut = useMutation({
    mutationFn: async (id: string) => { await api.delete(`/reports/${id}`); },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['reports.counts'] });
      refetch();
      toast.show('Segnalazione eliminata', 'success');
    },
  });

  async function handleDelete(r: ExistingReport): Promise<void> {
    const ok = await confirm({
      title: 'Elimina segnalazione', message: 'Eliminare questa segnalazione?',
      confirmLabel: 'Elimina', variant: 'danger',
    });
    if (ok) delMut.mutate(r.id);
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: 560 }}>
        <h2>📋 Segnalazioni — {targetLabel}</h2>

        <div style={{ marginTop: 14, marginBottom: 16, maxHeight: 260, overflowY: 'auto' }}>
          {existing.length === 0 ? (
            <div style={{ textAlign: 'center', color: '#999', padding: 16, fontStyle: 'italic' }}>
              Nessuna segnalazione presente
            </div>
          ) : (
            existing.map(r => (
              <div key={r.id} style={{
                display: 'flex', gap: 8, padding: 10, marginBottom: 6,
                background: '#fff3cd', border: '1px solid #ffeaa7', borderRadius: 6,
              }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 14, color: '#333' }}>{r.note}</div>
                  <div style={{ fontSize: 11, color: '#888', marginTop: 4 }}>
                    {new Date(r.createdAt).toLocaleString('it-IT')} · {r.reportedByName}
                  </div>
                </div>
                <button onClick={() => handleDelete(r)} title="Elimina"
                  style={{ background: '#dc3545', color: 'white', border: 'none', borderRadius: 4,
                           padding: '4px 8px', cursor: 'pointer', alignSelf: 'flex-start' }}>🗑️</button>
              </div>
            ))
          )}
        </div>

        <div className="form-group" style={{ borderTop: '1px solid #eee', paddingTop: 12 }}>
          <label style={{ fontWeight: 700 }}>Nuova segnalazione</label>
          <textarea value={note} onChange={e => setNote(e.target.value)}
            placeholder="Descrivi il problema..." autoFocus
            style={{ width: '100%', minHeight: 70, padding: 8, border: '1px solid #ced4da', borderRadius: 6 }} />
        </div>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 10 }}>
          <button onClick={onClose} className="btn-secondary">Chiudi</button>
          <button onClick={() => addMut.mutate()} disabled={!note || addMut.isPending} className="btn-warning">
            ➕ Aggiungi
          </button>
        </div>
      </div>
    </div>
  );
}
